import { useState, useCallback, useEffect } from 'react';
import { useAuthenticatedFetch } from '../utils/api';

/**
 * Reusable optimization type hook
 * Manages optimization type state (regular, moderate, extreme)
 * Fetches default from shop's settings
 * 
 * @param {string} initialType - Initial optimization type (default: 'regular', or shop's default)
 * @returns {Object} Optimization type state and handlers
 */
function useOptimizationType(initialType = 'regular') {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [optimizationType, setOptimizationType] = useState(initialType);
  const [defaultFetched, setDefaultFetched] = useState(false);

  // Fetch shop's default optimization type on mount
  useEffect(() => {
    // Only fetch once
    if (defaultFetched) return;
    
    async function fetchDefault() {
      try {
        const response = await authenticatedFetch('/api/optimization-types/default');
        const data = await response.json();
        if (data.success && data.type) {
          setOptimizationType(data.type.key);
        }
      } catch (error) {
        console.error('Error fetching default optimization type:', error);
        // Keep initialType on error
      } finally {
        setDefaultFetched(true);
      }
    }
    fetchDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only fetch once on mount

  const handleOptimizationTypeChange = useCallback((type) => {
    // Validation will be done on backend - just set the type if provided
    // Frontend validation is not needed since backend validates against DB
    if (type && typeof type === 'string') {
      setOptimizationType(type);
    }
  }, []);

  const getOptimizationTypeDetails = useCallback(() => {
    // This will be populated from API response in components
    const details = {
      regular: 'Balanced compression with minimal quality loss',
      moderate: 'Higher compression with visible size reduction',
      extreme: 'Maximum compression for smallest size'
    };
    return details[optimizationType] || '';
  }, [optimizationType]);

  return {
    optimizationType,
    setOptimizationType,
    handleOptimizationTypeChange,
    getOptimizationTypeDetails
  };
}

export default useOptimizationType;

